import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import { cloudinary } from "./cloudinary";

export interface CldVideoRef {
  play: () => Promise<void>;
  pause: () => void;
  seek: (time: number) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
}

const CldVideo = forwardRef<
  CldVideoRef,
  { cloudinaryId: string } & React.HTMLAttributes<HTMLDivElement>
>(({ cloudinaryId, ...props }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = cloudinary
      .videoTag(cloudinaryId, {
        controls: true,
        width: "100%",
      })
      .toDOM();

    if (containerRef.current) {
      containerRef.current.appendChild(video);
      videoRef.current = containerRef.current.querySelector("video");
    }
  }, [cloudinaryId]);

  useImperativeHandle(ref, () => ({
    play: async () => {
      if (videoRef.current) {
        try {
          await videoRef.current.play();
        } catch (error) {
          console.error("Video play failed:", error);
        }
      } else {
        return Promise.resolve();
      }
    },
    pause: () => videoRef.current?.pause(),
    seek: (time: number) => {
      if (videoRef.current) videoRef.current.currentTime = time;
    },
    getCurrentTime: () => videoRef.current?.currentTime || 0,
    getDuration: () => videoRef.current?.duration || 0,
  }));

  return <div ref={containerRef} {...props} />;
});

export default CldVideo;
